import { IGlobalCreator } from "@shared/lib/types";
import { Button, Modal } from "@shared/ui";
import SuggestDiscoveryCampaignForm from "@shared/ui/InfluencerTable/SuggestDiscoveryCampaignForm";
import { FileSpreadsheet } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { readFollowerCount } from "../CreatorRosterAdd";
import { socialProfileUrl } from "../socialProfileUrl";
import pageStyles from "../styles.module.scss";
import { AddCreatorBar } from "./AddCreatorBar";
import { CreatorFitSelectionFooter } from "./CreatorFitSelectionFooter";
import { CreatorFitTable, isCheckAnalyzing } from "./CreatorFitTable";
import styles from "./creatorFitFlow.module.scss";
import {
  CreatorFitCheck,
  exportCreatorFitChecksToExcel,
} from "./exportCreatorFitExcel";

export function CreatorFitCreators({
  checks,
  canAdd,
  busy,
  onAdd,
  onOpenSheet,
  requestMasterPromptId,
  promptChanged,
  reanalyzingId,
  isReanalyzing,
  onReanalyze,
}: {
  checks: CreatorFitCheck[];
  canAdd: boolean;
  busy: boolean;
  onAdd: Parameters<typeof AddCreatorBar>[0]["onSubmit"];
  onOpenSheet: () => void;
  requestMasterPromptId?: string;
  promptChanged: boolean;
  reanalyzingId: string | null;
  isReanalyzing: boolean;
  onReanalyze: (checkId: string) => void;
}) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);

  useEffect(() => {
    const valid = new Set(checks.map((c) => c._id));
    setSelectedRowKeys((prev) => prev.filter((id) => valid.has(String(id))));
  }, [checks]);

  const selectedChecks = useMemo(
    () => checks.filter((c) => selectedRowKeys.includes(c._id)),
    [checks, selectedRowKeys]
  );

  const handleDeselectAll = () => {
    setSelectedRowKeys([]);
  };

  const handleExportExcel = () => {
    exportCreatorFitChecksToExcel(selectedChecks);
  };

  return (
    <section
      className={`${styles.panel} ${pageStyles.creatorFitTab} ${
        selectedRowKeys.length > 0 ? styles.panelWithSelection : ""
      }`}
    >
      <div className={styles.panelHead}>
        <div className={styles.titleLine}>
          <span className={styles.step}>2</span>
          <h2 className={styles.panelTitle}>Creators</h2>
          <div className={styles.headActions}>
            <span className={styles.count}>
              {checks.filter((check) => !isCheckAnalyzing(check, null, false)).length}{" "}
              scored
              {checks.some((check) => isCheckAnalyzing(check, null, false))
                ? " · analyzing…"
                : ""}
            </span>
            <Button
              size="small"
              icon={<FileSpreadsheet size={13} />}
              disabled={!canAdd || busy}
              onClick={onOpenSheet}
            >
              Bulk add
            </Button>
          </div>
        </div>
        <p className={styles.panelHint}>
          Add people to score against the criteria above.
        </p>
      </div>
      <div className={styles.panelBody}>
        <AddCreatorBar disabled={!canAdd || busy} onSubmit={onAdd} />
        <p className={styles.addHint}>
          {canAdd
            ? "Add one handle, or use Bulk add to import a sheet and analyze many at once."
            : "Write the criteria above before adding creators."}
        </p>
      </div>
      {checks.length === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>No scores yet</p>
          <p className={styles.emptyCopy}>
            Type a username above, or bulk-add a sheet. Each creator is scored
            against your criteria.
          </p>
        </div>
      ) : (
        <CreatorFitTable
          checks={checks}
          requestMasterPromptId={requestMasterPromptId}
          promptChanged={promptChanged}
          reanalyzingId={reanalyzingId}
          isReanalyzing={isReanalyzing}
          onReanalyze={onReanalyze}
          selectedRowKeys={selectedRowKeys}
          onSelectChange={setSelectedRowKeys}
        />
      )}

      <CreatorFitSelectionFooter
        selectedCount={selectedRowKeys.length}
        onClear={handleDeselectAll}
        onExportExcel={handleExportExcel}
        onSuggestCampaign={() => setIsSuggestModalOpen(true)}
      />

      <Modal
        title="Suggest Creators in Campaign / Brief"
        open={isSuggestModalOpen}
        onCancel={() => setIsSuggestModalOpen(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        <SuggestDiscoveryCampaignForm
          creators={selectedChecks.map((check) => {
            const sm = check.socialMedia;
            const platform = sm?.platform?.toLowerCase() || "instagram";
            const username = sm?.username || "";
            const profileUrl =
              socialProfileUrl(platform, username) ||
              (username ? `https://www.${platform}.com/${username}` : "");
            const followers = readFollowerCount(sm) || 0;

            return {
              url: profileUrl,
              platform_username: username,
              work_platform: { name: platform },
              follower_count: followers,
              subscriber_count: followers,
            } as unknown as IGlobalCreator;
          })}
          onClose={() => {
            setIsSuggestModalOpen(false);
            handleDeselectAll();
          }}
        />
      </Modal>
    </section>
  );
}

